/**
 * streakFacts — de ENIGE manier om een "streak" uit een leerlingstaat te lezen.
 *
 * WAAROM DIT BESTAAT
 *
 * "Streak" bleek vier verschillende dingen in dit product, en dat is precies één
 * woord te veel voor twee begrippen:
 *
 *   1. `streak.currentTotalStreak` — hoeveel vragen ACHTER ELKAAR goed. Loopt op
 *      per goed antwoord, staat op nul bij het eerste foute. Dit is de vlam in de
 *      leerling-shell.
 *   2. `dailyStreak.currentWeekDays` — op hoeveel DAGEN deze week het dagdoel is
 *      gehaald. Loopt op met maximaal één per dag.
 *
 * Die twee hebben niets met elkaar te maken: de eerste kan 12 zijn op een dag dat
 * de tweede 0 is. Toch werden ze door elkaar gebruikt — het leerkrachtportaal zette
 * een antwoordreeks onder het kopje "Dagen Streak", en het ouderportaal las een
 * veld `stats.streakState.currentStreak` dat op `UserStats` **niet bestaat** en dus
 * altijd 0 opleverde. De felicitatie "3 dagen op rij geoefend!" kon daardoor nooit
 * verschijnen.
 *
 * Dat laatste is het gevaarlijke patroon: `?? 0` maakt een niet-bestaand veld
 * ononderscheidbaar van een echte nul. Er is geen foutmelding, geen typefout, geen
 * lege plek op het scherm — de tip verschijnt gewoon nooit, en niemand kan zien dat
 * er iets mist.
 *
 * DAAROM TWEE FUNCTIES MET VERSCHILLENDE NAMEN
 *
 * Niet één `getStreak()` met een parameter. De aanroeper moet KIEZEN welk van de
 * twee begrippen hij bedoelt, en de naam van de functie moet in de code te lezen
 * zijn naast het label dat op het scherm komt. Zo is "Dagen Streak" naast
 * `getAnswerStreak()` meteen zichtbaar fout.
 */

import type { AppState } from '../app/types';

/**
 * Aantal vragen dat dit kind ACHTER ELKAAR goed had.
 *
 * Breekt bij het eerste foute antwoord én bij overslaan. Gebruik dit voor
 * momentum-in-de-sessie (de vlam), nooit voor iets met "dagen" in het label.
 */
export function getAnswerStreak(state: AppState | null | undefined): number {
  return state?.streak?.currentTotalStreak ?? 0;
}

/** De langste antwoordreeks ooit — voor een persoonlijk record, niet voor voortgang. */
export function getLongestAnswerStreak(state: AppState | null | undefined): number {
  return state?.streak?.longestTotalStreak ?? 0;
}

/**
 * Op hoeveel dagen deze week het dagdoel is gehaald.
 *
 * Dit is wat een ouder of leerkracht bedoelt met "dagen op rij": ritme, niet
 * scherpte. Maximaal één per dag, en het loopt met de week mee.
 */
export function getPracticeDaysThisWeek(state: AppState | null | undefined): number {
  return state?.dailyStreak?.currentWeekDays ?? 0;
}

/** Haalde dit kind zijn dagdoel vandaag al? */
export function hasReachedDailyGoal(state: AppState | null | undefined): boolean {
  return state?.dailyStreak?.todayGoalReached ?? false;
}
